import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { apiPatch, apiPost } from '../../api/client';
import type { OrgUserRow } from './superOrgTypes';
import { OrgAiPanel, OrgUsageSpendPanel, superOrgBtn, superOrgField, superOrgInput } from './superOrgPanels';
import type { SuperOrgDetailOutletContext } from './SuperOrgDetailLayout';

export function SuperOrgUsuariosPage() {
  const { org, token, reload, setPageErr } = useOutletContext<SuperOrgDetailOutletContext>();
  const [userEmail, setUserEmail] = useState('');
  const [userPass, setUserPass] = useState('');
  const [userName, setUserName] = useState('');
  const [userRoleId, setUserRoleId] = useState('');
  const [tempPasswordModal, setTempPasswordModal] = useState<{ email: string; password: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const newUserRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (searchParams.get('nuevo') !== '1' || !newUserRef.current) return;
    newUserRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const t = window.setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('nuevo');
        return next;
      }, { replace: true });
    }, 400);
    return () => window.clearTimeout(t);
  }, [searchParams, setSearchParams, org.id]);

  useEffect(() => {
    const master = org.roles?.find((r) => r.key === 'master_admin');
    const fallback = org.roles?.[0]?.id ?? '';
    if (!userRoleId && (master?.id || fallback)) {
      setUserRoleId(master?.id ?? fallback);
    }
  }, [org.roles, userRoleId]);

  const assignRole = useCallback(
    async (u: OrgUserRow, organizationRoleId: string) => {
      setPageErr('');
      try {
        await apiPatch(
          `/api/super/organizations/${org.id}/users/${u.id}/role`,
          { organizationRoleId },
          token,
          { successMessage: 'Rol actualizado' }
        );
        await reload();
      } catch (ex) {
        setPageErr((ex as Error).message);
      }
    },
    [org.id, token, reload, setPageErr]
  );

  const resetPassword = useCallback(
    async (u: OrgUserRow) => {
      if (
        !window.confirm(
          `¿Generar una contraseña temporal para ${u.email}? El usuario deberá definir una nueva al iniciar sesión.`
        )
      ) {
        return;
      }
      setPageErr('');
      try {
        const data = await apiPost<{ temporaryPassword: string; user: { email: string } }>(
          `/api/super/organizations/${org.id}/users/${u.id}/reset-password`,
          {},
          token,
          {}
        );
        setTempPasswordModal({ email: data.user.email, password: data.temporaryPassword });
        await reload();
      } catch (ex) {
        setPageErr((ex as Error).message);
      }
    },
    [org.id, token, reload, setPageErr]
  );

  const createUser = useCallback(async () => {
    setPageErr('');
    try {
      await apiPost(
        `/api/super/organizations/${org.id}/users`,
        {
          email: userEmail.trim(),
          password: userPass,
          name: userName.trim() || undefined,
          organizationRoleId: userRoleId,
        },
        token,
        { successMessage: 'Usuario creado' }
      );
      setUserEmail('');
      setUserPass('');
      setUserName('');
      await reload();
    } catch (ex) {
      setPageErr((ex as Error).message);
    }
  }, [org.id, token, userEmail, userPass, userName, userRoleId, reload, setPageErr]);

  return (
    <div>
      {tempPasswordModal ? (
        <div
          role="dialog"
          aria-modal
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              maxWidth: 440,
              width: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0f172a' }}>Contraseña temporal</h3>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#64748b' }}>
              Entregá esta contraseña a <strong>{tempPasswordModal.email}</strong>. Solo se muestra una vez.
            </p>
            <code
              style={{
                display: 'block',
                padding: '12px 14px',
                background: '#f8fafc',
                borderRadius: 8,
                fontSize: 15,
                wordBreak: 'break-all',
                marginBottom: 16,
                border: '1px solid #e2e8f0',
              }}
            >
              {tempPasswordModal.password}
            </code>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                style={superOrgBtn}
                onClick={() => {
                  void navigator.clipboard.writeText(tempPasswordModal.password);
                }}
              >
                Copiar
              </button>
              <button type="button" style={{ ...superOrgBtn, background: '#64748b' }} onClick={() => setTempPasswordModal(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <h2 style={{ fontSize: 17, margin: '0 0 16px', color: '#0f172a' }}>Usuarios de la consola</h2>
      {org.users.length === 0 ? (
        <p style={{ fontSize: 14, color: '#94a3b8', margin: '0 0 20px' }}>Aún no hay usuarios.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 24, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: '#f8fafc', color: '#64748b' }}>
                <th style={{ padding: '10px 14px' }}>Email</th>
                <th style={{ padding: '10px 14px' }}>Nombre</th>
                <th style={{ padding: '10px 14px' }}>Rol</th>
                <th style={{ padding: '10px 14px' }}>Estado</th>
                <th style={{ padding: '10px 14px' }}>Alta</th>
                <th style={{ padding: '10px 14px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {org.users.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 14px' }}>{u.email}</td>
                  <td style={{ padding: '10px 14px' }}>{u.name ?? '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <select
                      value={u.organizationRoleId ?? ''}
                      onChange={(e) => void assignRole(u, e.target.value)}
                      style={{ fontSize: 13, maxWidth: '100%' }}
                    >
                      <option value="" disabled>
                        Sin rol
                      </option>
                      {(org.roles ?? []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {u.mustChangePassword ? (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#b45309',
                          background: '#fffbeb',
                          padding: '4px 8px',
                          borderRadius: 6,
                        }}
                      >
                        Debe cambiar contraseña
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#64748b' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <button
                      type="button"
                      style={{ ...superOrgBtn, fontSize: 12, padding: '6px 10px' }}
                      onClick={() => void resetPassword(u)}
                    >
                      Nueva contraseña temporal
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div ref={newUserRef}>
        <h3 style={{ fontSize: 15, margin: '0 0 12px' }}>Nuevo usuario</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={superOrgField}>
            Email
            <input
              style={superOrgInput}
              type="email"
              placeholder="email@empresa.com"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
            />
          </label>
          <label style={superOrgField}>
            Contraseña (mín. 8)
            <input
              style={superOrgInput}
              type="password"
              value={userPass}
              onChange={(e) => setUserPass(e.target.value)}
            />
          </label>
          <label style={superOrgField}>
            Nombre (opcional)
            <input
              style={superOrgInput}
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />
          </label>
          <label style={superOrgField}>
            Rol
            <select
              style={superOrgInput}
              value={userRoleId}
              onChange={(e) => setUserRoleId(e.target.value)}
            >
              {(org.roles ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <button type="button" style={superOrgBtn} onClick={() => void createUser()}>
            Guardar usuario
          </button>
        </div>
      </div>
    </div>
  );
}

export function SuperOrgConsumoPage() {
  const { org, token } = useOutletContext<SuperOrgDetailOutletContext>();
  return (
    <div>
      <h2 style={{ fontSize: 17, margin: '0 0 8px', color: '#0f172a' }}>Consumo y costo</h2>
      <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 16px' }}>
        Período y totales aproximados para <strong>{org.name}</strong>.
      </p>
      <OrgUsageSpendPanel orgId={org.id} token={token} compact />
    </div>
  );
}

export function SuperOrgModelosPage() {
  const { org, token, reload, setPageErr } = useOutletContext<SuperOrgDetailOutletContext>();
  return (
    <div>
      <h2 style={{ fontSize: 17, margin: '0 0 8px', color: '#0f172a' }}>Modelos habilitados</h2>
      <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 16px' }}>
        Catálogo permitido y modelo por defecto para <strong>{org.name}</strong>.
      </p>
      <OrgAiPanel
        orgId={org.id}
        token={token}
        ai={org.ai}
        onUpdated={() => {
          void reload();
        }}
        onError={setPageErr}
        compact
      />
    </div>
  );
}
